import React from 'react';
import { AlertTriangle } from 'lucide-react';
import {
    QboBillRecord,
    QboGeneralLedgerRecord,
    QboInvoiceRecord,
    QboLineItem,
    QboTransactionListItem,
    UploadedLedgerRecord,
} from '../../../api/types';
import formatCurrency from '../../../utils/formatCurrency';
import { DrawerBadge, DrawerField } from '../shared/LedgerDrawerPrimitives';

type DrawerPresentation = {
    title: string;
    subtitle: React.ReactNode;
    amountDisplay: React.ReactNode;
    metadataContent: React.ReactNode;
};

export const buildTransactionListDrawerPresentation = ({
    txn,
    formatDate,
    formatTitleCase,
    formatTransactionAmount,
    onOpenQboTransaction,
}: {
    txn: QboTransactionListItem;
    formatDate: (value?: string) => string;
    formatTitleCase: (value?: string | null) => string;
    formatTransactionAmount: (amount?: number, currency?: string, amountMissing?: boolean, amountDerived?: boolean) => string;
    onOpenQboTransaction?: (transactionId: string) => void;
}): DrawerPresentation => {
    const qboTransactionId = String(txn.data.transaction_type?.id || '').trim();
    const hasTransactionLink = !!qboTransactionId && !!onOpenQboTransaction;
    const counterpartyName = txn.data.counterparty?.name || txn.data.customer?.name || txn.data.vendor?.name;
    const transactionTypeName = txn.data.transaction_type?.name || 'QBO Transaction';
    const journalEntry = txn.data.journal_entry;
    const journalLines = journalEntry?.lines || [];

    return {
        title: counterpartyName || transactionTypeName || 'Transaction',
        subtitle: txn.data.transaction_type?.name || 'QBO Transaction',
        amountDisplay: (
            <div className="flex items-center gap-2 mt-2">
                <span className="text-3xl font-bold text-gray-900">
                    {formatTransactionAmount(
                        txn.data.amount,
                        txn.data.currency || 'USD',
                        txn.data.amount_missing,
                        txn.data.amount_derived_from_journal_lines,
                    )}
                </span>
                <DrawerBadge className="bg-blue-100 text-blue-800">QBO</DrawerBadge>
                {txn.data.amount_derived_from_journal_lines && (
                    <DrawerBadge className="bg-slate-100 text-slate-700">Derived From Journal Lines</DrawerBadge>
                )}
            </div>
        ),
        metadataContent: (
            <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                    <DrawerField label="Date" value={formatDate(txn.data.transaction_date)} />
                    <DrawerField label="Document #" value={txn.data.document_number || '—'} />
                    <DrawerField label="Type" value={transactionTypeName} />
                    <DrawerField label="Counterparty" value={counterpartyName || '—'} />
                    <DrawerField label="Account" value={txn.data.account?.name || '—'} />
                    <DrawerField label="Split Account" value={txn.data.split_account?.name || '—'} />
                    <DrawerField label="Memo" value={txn.data.memo || '—'} />
                    <DrawerField
                        label="Linked QBO Record"
                        value={hasTransactionLink ? (
                            <button
                                type="button"
                                className="text-left text-indigo-600 hover:underline"
                                onClick={() => onOpenQboTransaction?.(qboTransactionId)}
                            >
                                Open QBO Transaction
                            </button>
                        ) : '—'}
                    />
                </div>

                {journalEntry && (
                    <div className="space-y-2">
                        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Debit / Credit Lines</h3>
                        <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
                            <div className="grid grid-cols-2 gap-4">
                                <DrawerField label="Line Count" value={journalEntry.line_count ?? journalLines.length} />
                                <DrawerField label="Journal Memo" value={journalEntry.memo || txn.data.memo || '—'} />
                                <DrawerField label="Debit Total" value={formatCurrency(journalEntry.debit_total, txn.data.currency || 'USD')} />
                                <DrawerField label="Credit Total" value={formatCurrency(journalEntry.credit_total, txn.data.currency || 'USD')} />
                            </div>
                            {journalLines.length > 0 && (
                                <div className="max-h-72 overflow-auto rounded-lg border border-gray-100">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                                                <th className="px-4 py-3">Posting</th>
                                                <th className="px-4 py-3">Account</th>
                                                <th className="px-4 py-3">Description</th>
                                                <th className="px-4 py-3">Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 bg-white">
                                            {journalLines.map((line, index) => (
                                                <tr key={`${line.account_name || 'line'}-${index}`} className="text-sm text-gray-700">
                                                    <td className="px-4 py-3">{formatTitleCase(line.posting_type) || '—'}</td>
                                                    <td className="px-4 py-3">{line.account_name || '—'}</td>
                                                    <td className="px-4 py-3">{line.description || '—'}</td>
                                                    <td className="px-4 py-3">{formatCurrency(line.amount, txn.data.currency || 'USD')}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        ),
    };
};

export const buildGeneralLedgerDrawerPresentation = ({
    gl,
    formatDate,
}: {
    gl: QboGeneralLedgerRecord;
    formatDate: (value?: string) => string;
}): DrawerPresentation => ({
    title: gl.name || gl.document_number || gl.transaction_type || 'General Ledger Entry',
    subtitle: gl.transaction_type || 'General Ledger',
    amountDisplay: (
        <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className="text-3xl font-bold text-gray-900">
                {formatCurrency(gl.debit || gl.credit || 0, gl.currency || 'USD')}
            </span>
            <DrawerBadge className="bg-slate-100 text-slate-800">General Ledger</DrawerBadge>
        </div>
    ),
    metadataContent: (
        <div className="grid grid-cols-2 gap-4">
            <DrawerField label="Date" value={formatDate(gl.transaction_date)} />
            <DrawerField label="Document #" value={gl.document_number || '—'} />
            <DrawerField label="Name" value={gl.name || '—'} />
            <DrawerField label="Type" value={gl.transaction_type || '—'} />
            <DrawerField label="Account" value={gl.account_name || '—'} />
            <DrawerField label="Split Account" value={gl.split_account || '—'} />
            <DrawerField label="Debit" value={gl.debit ? formatCurrency(gl.debit, gl.currency || 'USD') : '—'} />
            <DrawerField label="Credit" value={gl.credit ? formatCurrency(gl.credit, gl.currency || 'USD') : '—'} />
            <DrawerField label="Balance" value={typeof gl.balance === 'number' ? formatCurrency(gl.balance, gl.currency || 'USD') : '—'} />
            <DrawerField label="Period" value={gl.period_start || gl.period_end ? `${formatDate(gl.period_start)} - ${formatDate(gl.period_end)}` : '—'} />
            <DrawerField label="Memo" value={gl.memo || '—'} />
            <DrawerField label="Account Type" value={gl.account_type || '—'} />
        </div>
    ),
});

export const buildQboInvoiceDrawerPresentation = ({
    invoice,
    formatDate,
}: {
    invoice: QboInvoiceRecord;
    formatDate: (value?: string) => string;
}): DrawerPresentation => ({
    title: invoice.data.invoice_number || invoice.data.invoice_id || 'Invoice',
    subtitle: invoice.data.customer?.name || 'Customer',
    amountDisplay: (
        <div className="flex items-center gap-2 mt-2">
            <span className="text-3xl font-bold text-gray-900">
                {formatCurrency(invoice.data.total_amount, invoice.data.currency || 'USD')}
            </span>
            <DrawerBadge className="bg-blue-100 text-blue-800">QBO Invoice</DrawerBadge>
        </div>
    ),
    metadataContent: (
        <div className="grid grid-cols-2 gap-4">
            <DrawerField label="Date" value={formatDate(invoice.data.transaction_date)} />
            <DrawerField label="Due Date" value={formatDate(invoice.data.due_date)} />
            <DrawerField label="Customer" value={invoice.data.customer?.name || '—'} />
            <DrawerField label="Balance Due" value={formatCurrency(invoice.data.balance_due, invoice.data.currency || 'USD')} />
        </div>
    ),
});

export const buildQboBillDrawerPresentation = ({
    bill,
    formatDate,
    apBillStatus,
}: {
    bill: QboBillRecord;
    formatDate: (value?: string) => string;
    apBillStatus: string;
}): DrawerPresentation => {
    const qboDocNumber = (bill as QboBillRecord & { data?: { raw?: { DocNumber?: string } } })?.data?.raw?.DocNumber;
    const getApBillStatusTone = (status?: string | null) => {
        const normalized = String(status || '').trim().toLowerCase();
        if (normalized === 'paid') return 'bg-emerald-100 text-emerald-800';
        if (normalized === 'overdue') return 'bg-rose-100 text-rose-800';
        if (normalized === 'due this week') return 'bg-amber-100 text-amber-800';
        return 'bg-slate-100 text-slate-700';
    };

    return {
        title: bill.data.bill_id || 'Bill',
        subtitle: bill.data.vendor?.name || 'Vendor',
        amountDisplay: (
            <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className="text-3xl font-bold text-gray-900">
                    {formatCurrency(bill.data.total_amount, bill.data.currency || 'USD')}
                </span>
                <DrawerBadge className="bg-blue-100 text-blue-800">AP Bill</DrawerBadge>
                <DrawerBadge className={getApBillStatusTone(apBillStatus)}>{apBillStatus}</DrawerBadge>
            </div>
        ),
        metadataContent: (
            <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                    <DrawerField label="Vendor" value={bill.data.vendor?.name || '—'} />
                    <DrawerField label="Status" value={apBillStatus || '—'} />
                    <DrawerField label="QBO Bill ID" value={bill.data.bill_id || '—'} />
                    <DrawerField label="Doc Number" value={qboDocNumber || '—'} />
                    <DrawerField label="Bill Date" value={formatDate(bill.data.transaction_date)} />
                    <DrawerField label="Due Date" value={formatDate(bill.data.due_date)} />
                    <DrawerField label="Total Amount" value={formatCurrency(bill.data.total_amount, bill.data.currency || 'USD')} />
                    <DrawerField label="Open Amount" value={formatCurrency(bill.data.balance_due, bill.data.currency || 'USD')} />
                </div>
            </div>
        ),
    };
};

export const buildUploadedDrawerPresentation = ({
    uploadedRecord,
    isUploadedInvoice,
    isUploadedExpense,
    uploadedStatus,
    canOpenDocument,
    displayFileName,
    formatDate,
    getDocumentVendorName,
    onOpenDocumentFile,
}: {
    uploadedRecord: UploadedLedgerRecord;
    isUploadedInvoice: boolean;
    isUploadedExpense: boolean;
    uploadedStatus?: string;
    canOpenDocument: boolean;
    displayFileName: string;
    formatDate: (value?: string) => string;
    getDocumentVendorName: (record: UploadedLedgerRecord, fallbackLabel?: string) => string;
    onOpenDocumentFile?: (fileId: string, source: 'uploaded_invoices' | 'uploaded_expenses' | 'uploaded_bills') => void;
}): DrawerPresentation => ({
    title: uploadedRecord?.data.invoice_number || (isUploadedExpense ? 'Uploaded Expense' : 'Uploaded Document'),
    subtitle: getDocumentVendorName(uploadedRecord, isUploadedInvoice ? 'Customer' : 'Vendor'),
    amountDisplay: (
        <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className="text-3xl font-bold text-gray-900">
                {formatCurrency(uploadedRecord?.data.total_amount, uploadedRecord?.data.currency || 'USD')}
            </span>
            <DrawerBadge className={isUploadedExpense ? 'bg-cyan-100 text-cyan-800' : 'bg-amber-100 text-amber-800'}>
                {isUploadedInvoice ? 'Uploaded Invoice' : isUploadedExpense ? 'Uploaded Expense' : 'Uploaded Bill'}
            </DrawerBadge>
            {uploadedStatus && (
                <DrawerBadge className={uploadedStatus === 'confirmed' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}>
                    {uploadedStatus.replace(/_/g, ' ')}
                </DrawerBadge>
            )}
        </div>
    ),
    metadataContent: (
        <div className="grid grid-cols-2 gap-4">
            <DrawerField label="Date" value={formatDate(uploadedRecord?.data.transaction_date)} />
            <DrawerField label="Due Date" value={formatDate(uploadedRecord?.data.due_date)} />
            <DrawerField label={isUploadedInvoice ? 'Customer' : 'Vendor'} value={getDocumentVendorName(uploadedRecord, '—') || '—'} />
            <DrawerField label="Balance Due" value={formatCurrency(uploadedRecord?.data.balance_due, uploadedRecord?.data.currency || 'USD')} />
            <DrawerField
                label="File"
                value={canOpenDocument ? (
                    <button
                        type="button"
                        className="text-left text-indigo-600 hover:underline break-all"
                        onClick={() => {
                            if (uploadedRecord?.file_id && onOpenDocumentFile) {
                                onOpenDocumentFile(
                                    uploadedRecord.file_id,
                                    isUploadedInvoice ? 'uploaded_invoices' : isUploadedExpense ? 'uploaded_expenses' : 'uploaded_bills',
                                );
                            }
                        }}
                    >
                        {displayFileName}
                    </button>
                ) : displayFileName}
            />
            <DrawerField label="Last Updated" value={formatDate(uploadedRecord?.data.metadata?.last_updated_time)} />
        </div>
    ),
});

export const UploadedPriceSignalsSection: React.FC<{
    uploadedRecord: UploadedLedgerRecord;
}> = ({ uploadedRecord }) => {
    const priceIncreaseCount = uploadedRecord?.price_increases?.length || 0;
    const priceDecreaseCount = uploadedRecord?.price_decreases?.length || 0;

    if (priceIncreaseCount <= 0 && priceDecreaseCount <= 0) {
        return null;
    }

    return (
        <div className="space-y-3">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Price Signals</h3>
            <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-rose-600">Increases</p>
                    <p className="mt-2 text-2xl font-bold text-rose-900">{priceIncreaseCount}</p>
                </div>
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">Decreases</p>
                    <p className="mt-2 text-2xl font-bold text-emerald-900">{priceDecreaseCount}</p>
                </div>
            </div>

            {priceIncreaseCount > 0 && (
                <div className="rounded-xl border border-rose-200 overflow-hidden">
                    <div className="px-4 py-3 bg-rose-50 text-sm font-medium text-rose-900 flex items-center gap-2">
                        <AlertTriangle size={16} />
                        Price increases
                    </div>
                    <div className="divide-y divide-rose-100">
                        {uploadedRecord.price_increases?.map((item, index) => (
                            <div key={`increase-${index}`} className="px-4 py-3 text-sm">
                                <div className="font-medium text-gray-900">{item.description || 'Line item'}</div>
                                <div className="text-gray-600 mt-1">
                                    {item.currency || uploadedRecord.data.currency || 'USD'} {item.previous_most_recent_unit_price?.toFixed?.(2) || '0.00'} to {item.currency || uploadedRecord.data.currency || 'USD'} {item.unit_price?.toFixed?.(2) || '0.00'}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {priceDecreaseCount > 0 && (
                <div className="rounded-xl border border-emerald-200 overflow-hidden">
                    <div className="px-4 py-3 bg-emerald-50 text-sm font-medium text-emerald-900 flex items-center gap-2">
                        <AlertTriangle size={16} />
                        Price decreases
                    </div>
                    <div className="divide-y divide-emerald-100">
                        {uploadedRecord.price_decreases?.map((item, index) => (
                            <div key={`decrease-${index}`} className="px-4 py-3 text-sm">
                                <div className="font-medium text-gray-900">{item.description || 'Line item'}</div>
                                <div className="text-gray-600 mt-1">
                                    {item.currency || uploadedRecord.data.currency || 'USD'} {item.previous_most_recent_unit_price?.toFixed?.(2) || '0.00'} to {item.currency || uploadedRecord.data.currency || 'USD'} {item.unit_price?.toFixed?.(2) || '0.00'}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export const AccountingLineItemsSection: React.FC<{
    lineItems: QboLineItem[];
    currency: string;
    getLineItemQuantity: (lineItem?: QboLineItem) => number;
}> = ({ lineItems, currency, getLineItemQuantity }) => {
    if (lineItems.length <= 0) {
        return null;
    }

    return (
        <div className="space-y-3">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Line Items</h3>
            <div className="rounded-xl border border-gray-200 overflow-hidden">
                <div className="grid grid-cols-[minmax(0,1fr)_60px_96px] gap-3 px-4 py-3 bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <span className="truncate">Description</span>
                    <span className="text-right">Qty</span>
                    <span className="text-right">Amount</span>
                </div>
                <div className="divide-y divide-gray-100">
                    {lineItems.map((item, index) => (
                        <div key={`line-item-${index}`} className="grid grid-cols-[minmax(0,1fr)_60px_96px] gap-3 px-4 py-3 text-sm items-start">
                            <div className="min-w-0">
                                <div
                                    className="truncate font-medium text-gray-900"
                                    title={item.description || 'Line item'}
                                >
                                    {item.description || 'Line item'}
                                </div>
                                {item.unit_price !== undefined && (
                                    <div className="mt-1 truncate text-xs text-gray-500">
                                        Unit Price: {formatCurrency(
                                            typeof item.unit_price === 'number' ? item.unit_price : Number(item.unit_price?.$numberLong || 0),
                                            currency
                                        )}
                                    </div>
                                )}
                            </div>
                            <span className="text-right text-gray-600 whitespace-nowrap">{getLineItemQuantity(item)}</span>
                            <span className="text-right font-medium text-gray-900 whitespace-nowrap">
                                {formatCurrency(item.amount, currency)}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
